import {ObjectQueryFilter, Table} from "@tj/tj-dmp-lib-api";

export enum NumberFilterType {
    EQUALS = "Exactement",
    NOT_EXACTLY = "Différent de", // NEW
    LESS = "Inférieur",
    GREATER = "Supérieur",
    LESS_EQUALS = "Inférieur ou égal",
    GREATER_EQUALS = "Supérieur ou égal",
    EMPTY = "Est vide",
    NOT_EMPTY = "N'est pas vide",
    RANGE = "Plage" // NEW
}

export enum StringFilterType {
    STARTS_WITH = "Commence par",
    NOT_STARTS_WITH = "Ne commence pas par", // NEW
    EXACTLY = "Exactement",
    NOT_EXACTLY = "Différent de", // NEW
    CONTAINS = "Contient",
    NOT_CONTAINS = "Ne contient pas",
    EMPTY = "Est vide",
    NOT_EMPTY = "N'est pas vide",
    ENDS_WITH = "Fini par",
}

export enum DateFilterType {
    EXACTLY = "Exactement",
    NOT_EXACTLY = "Différent de", // NEW
    BEFORE = "Inférieur strict",
    BEFORE_EQUALS = "Inférieur ou égal", // NEW
    AFTER = "Supérieur strict",
    AFTER_EQUALS = "Supérieur ou égal", // NEW
    EMPTY = "Est vide",
    NOT_EMPTY = "N'est pas vide",
    RANGE = "Période",
    SLIDING_RANGE= "Période glissante" // NEW
}

/** Champs liste (valeurs à choisir dans une liste, TAG) */
export enum ListFilterType {
    EXACTLY = "Exactement",
    EMPTY = "Est vide",
    NOT_EMPTY = "N'est pas vide"
}

/** « Est vide » / « N'est pas vide », quel que soit le type du champ : pas de valeur à saisir */
const EMPTINESS_FILTER_TYPES: string[] = [
    StringFilterType.EMPTY, StringFilterType.NOT_EMPTY,
    NumberFilterType.EMPTY, NumberFilterType.NOT_EMPTY,
    DateFilterType.EMPTY, DateFilterType.NOT_EMPTY,
    ListFilterType.EMPTY, ListFilterType.NOT_EMPTY
];

export function isEmptinessFilterType(code: unknown): boolean {
    return EMPTINESS_FILTER_TYPES.includes(code as string);
}

export enum CampaignFilterType {
    IMPORT = "Campagne d'import",
    EXPORT = "Campagne d'export"
}

export enum NewsletterFilterType {
  SUBSCRIBED_TO = "Contacts abonnés à",
  NOT_SUBSCRIBED_TO = "Contacts non abonnés à",
  SUBSCRIBED_TO_ANY = "Contacts abonnés (au moins une newsletter)",
  NOT_SUBSCRIBED_TO_ANY = "Contacts non abonnés (aucune newsletter)"
}

const FILTER_TYPES_BY_VARIABLE_TYPE: { [variableType: string]: object } = {
    number: NumberFilterType,
    string: StringFilterType,
    date: DateFilterType,
    list: ListFilterType,
    campaign: CampaignFilterType,
    newsletter: NewsletterFilterType
};

/** Clé i18n d'un type de filtre ; le code stocké reste la valeur française de l'enum. */
export function filterTypeI18nKey(code: unknown, variableType?: string): string | null {
    const types = variableType && FILTER_TYPES_BY_VARIABLE_TYPE[variableType]
        ? [variableType, ...Object.keys(FILTER_TYPES_BY_VARIABLE_TYPE)]
        : Object.keys(FILTER_TYPES_BY_VARIABLE_TYPE);
    for (const type of types) {
        const entry = Object.entries(FILTER_TYPES_BY_VARIABLE_TYPE[type]).find(([, value]) => value === code);
        if (entry) {
            return `components.filter.operators.${type}.${entry[0]}`;
        }
    }
    return null;
}

export type ValueType =
    'TAG'
    | 'TECHNICAL'
    | 'EMAIL'
    | 'TEXT'
    | 'NUMBER'
    | 'DATE'
    | 'DATE_TIME'
    | 'TIME'
    | 'IATA_CITY'
    | 'IATA_AIRPORT'
    | 'IATA_COUNTRY'
    | 'CARRIER'
    | 'AFFILIATION'
    | 'LANG'
    | 'COUNTRY'
    | 'CIVILITY'
    | 'BOOLEAN'
    | 'PHONE'
    | 'MOBILE'
    | 'PRICE'
    | 'BOOKING_REFERENCE'
    | 'PRODUCT_TYPE'
    | 'ACTIVITY_STATUS'
    | 'LOYALTY_STATUS'
    | 'PURCHASE_STATUS'
    | 'CHANNEL'
    | 'NEWSLETTER';

/**
 * Types de valeur rendus par `dmp-select` sous forme de dropdown (sélection unique, modèle = un objet).
 * Tous les autres types utilisent un autocomplete multiple, dont le modèle doit être un tableau.
 */
export const SINGLE_SELECT_VALUE_TYPES: ValueType[] = [
    'CIVILITY',
    'LANG',
    'TAG',
    'ACTIVITY_STATUS',
    'LOYALTY_STATUS',
    'PURCHASE_STATUS',
    'CHANNEL'
];

export type ItemListField = {
    code: string;
    value: string;
}

export type Field = {
    name: string;
    label: string;
    variableType: 'string' | 'number' | 'boolean' | 'date' | 'list' | 'campaign';
    valueType: ValueType;
    schema: string;
    group?: string;
}

export type GroupField = {
    label: string;
    value: string;
    items: Field[]
}

export interface Filter<T = unknown, Enum = string|number> {
    schema?: string;
    field: Field;
    filterType?: {
        code: Enum, value: Enum
    };
    value: T;
    edit: boolean;
    typeGroup?: string;
    options?: any;
}

export interface NumberFilter extends Filter<number, NumberFilterType> {
    filterType: {
        code: NumberFilterType, value: NumberFilterType
    };
    value: number;
}

export interface StringFilter extends Filter<string, StringFilterType> {
    filterType: {
        code: StringFilterType, value: StringFilterType
    };
    value: string;
}

export interface BooleanFilter extends Filter<boolean> {
    value: boolean;
}

export interface DateFilter extends Filter<Date, DateFilterType> {
    filterType: {
        code: DateFilterType, value: DateFilterType
    };
    value: Date;
    options: { excludeYear: boolean };
}

export type Query = {
    datastore: string;
    partner: string;
    affectedPartners: string[];
    dataview: string;
    dataviewId: string;
    start: number;
    filter: ObjectQueryFilter,
    end: number,
    cols: string[]
}

export type InfoView = {
    datastore: string;
    partner: string;
    view: string;
    havingCountTable: Table[];
}
